import _ from 'lodash';
import { WORKSHEET_VIEW_KEYS } from '@/utilities/sharedDefinition.constants';
import { faCubes } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcScatterplot, fcTable, fcTreemap, fcTrend } from '@seeqdev/icons/seeq';
import type { IconDefinition } from '@seeqdev/icons';

/**
 * Identifies a plugin-supplied icon as being an SVG path instead of an icon class
 */
export const SVG_PATH = 'svgpath:';
export const WORKSHEET_TABSETS = {
  sidebar: ['search', 'investigate', 'annotate', 'reportComments', 'reportConfig'],
};
export type WorksheetTabset = keyof typeof WORKSHEET_TABSETS;
// Define valid targets for a view
export const WORKSHEET_TARGET = {
  WORKSHEET: 'WORKSHEET',
  DATA_PANEL: 'DATA_PANEL',
};

/**
 * Define available worksheet views here.
 *
 * Optional properties:
 *
 *   useSizeFromRender - an optional boolean property that flags a view as having non-scaling content
 *   (i.e. content that does not expand or collapse to fill the display pane). A view that sets this property
 *   to true should also add a ".useSizeFromRender" CSS class to the container of content in the HTML
 *   template. Doing these two things enables the screenshot service to properly screenshot the content and
 *   the report editor to know when content has a fixed size.
 */
export interface WorksheetView {
  key: string;
  target: string;
  name: string;
  // pluginViews() adds server-supplied views to this list, so this cannot narrow to IconDefinition.
  icon: string | IconDefinition;
  selectedItemsRealm: string;
  default?: boolean;
  useSizeFromRender?: boolean;
  hideFromViewSelector?: boolean;
  trackAction?: string;
}

export const WORKSHEET_VIEWS: WorksheetView[] = [
  {
    key: 'TREND',
    target: 'DATA_PANEL',
    name: 'TREND',
    icon: fcTrend,
    selectedItemsRealm: 'TREND',
    trackAction: 'Trend',
    default: true,
  },
  {
    key: 'SCATTER_PLOT',
    target: 'DATA_PANEL',
    name: 'SCATTER.XY_PLOT',
    icon: fcScatterplot,
    selectedItemsRealm: 'TREND',
    trackAction: 'Scatter Plot',
  },
  {
    key: 'TREEMAP',
    target: 'DATA_PANEL',
    name: 'TREEMAP.TITLE',
    icon: fcTreemap,
    selectedItemsRealm: 'TREND',
    trackAction: 'Treemap',
  },
  {
    key: 'TABLE',
    target: 'DATA_PANEL',
    name: 'TABLE_BUILDER.TITLE',
    icon: fcTable,
    useSizeFromRender: true,
    selectedItemsRealm: 'TABLE',
    trackAction: 'Table',
  },
];
export const ASSET_GROUP_VIEW: WorksheetView = {
  key: 'ASSET_GROUP_EDITOR',
  target: 'DATA_PANEL',
  name: 'ASSET_GROUP_EDITOR.TITLE',
  icon: faCubes,
  selectedItemsRealm: 'TREND',
  hideFromViewSelector: true,
};
export const TABLE_DEFINITION_VIEW: WorksheetView = {
  key: 'TABLE_DEFINITION_EDITOR',
  target: 'DATA_PANEL',
  name: 'TABLE_DEFINITION_EDITOR.TITLE',
  icon: faCubes,
  selectedItemsRealm: 'TREND',
  hideFromViewSelector: true,
};
export const ITEM_TABLE_EDITOR_VIEW: WorksheetView = {
  key: 'ITEM_TABLE_EDITOR',
  target: 'DATA_PANEL',
  name: 'ITEM_TABLE_EDITOR.TITLE',
  icon: faCubes,
  selectedItemsRealm: 'TREND',
  hideFromViewSelector: true,
};
// Manually constructed from WORKSHEET_VIEWS to give type safety
export const WORKSHEET_VIEW = {
  ...WORKSHEET_VIEW_KEYS,
  TABLE_DEFINITION_EDITOR: 'TABLE_DEFINITION_EDITOR',
  ITEM_TABLE_EDITOR: 'ITEM_TABLE_EDITOR',
};

/** The key identifying a worksheet view. Plugins contribute views too, so the set is open. */
export type WorksheetViewType = (typeof WORKSHEET_VIEW)[keyof typeof WORKSHEET_VIEW];
// Create a constant for each available sidebar tab
export const WORKSHEET_SIDEBAR_TAB = _.transform(
  WORKSHEET_TABSETS.sidebar,
  (result, tabname: string) => {
    result[_.replace(_.upperCase(tabname), ' ', '_')] = tabname;
  },
  {},
);
